import { useEffect, useState } from 'react';
import { connect } from 'redux-bundler-react';

import FormHeader from '@forms/components/Form/FormHeader';
import VerticalStepper from '@components/vertical-stepper/VerticalStepper';
import SectionButton from '@components/section-button/SectionButton';
import JDForm1a, { getJDForm1aMetadata } from '@forms/final-forms/JDForm1a';
import JDForm1b, { getJDForm1bMetadata } from '@forms/final-forms/JDForm1b';
import GPForm2b, { getGPForm2bMetadata } from '@forms/final-forms/GPForm2b';
import IPForm2a, { getIpForm2aMetadata } from '@forms/final-forms/IPForm2a';
import PermitForm, { getPermitFormMetadata } from '@forms/final-forms/PermitForm';
import PreAppForm3, { getPreAppForm3Metadata } from '@forms/final-forms/PreAppForm3';
import NonPcnForm, { getNonPcnFormMetadata } from '@src/app-pages/Forms/final-forms/NonPcnForm';
import { mapToPermitApplicationRequestType, saveRequest } from '@forms/input-forms/helpers';
import ViolationsForm5, { ViolationsForm5Metadata } from '@forms/final-forms/ViolationsForm5';
import { resolveFormDescriptor } from '@forms/registry/formRegistry';
import {
  RequestTypeStrings,
  ErrorStates,
  RequestStatus,
  UserPermissions,
  RequestTypeIds,
  RequestTypeStringIds,
} from '@src/utils/enums';
import { useRequest, useUserRoleCapabilities } from '@stores';
import { useNavigate } from '@tanstack/react-router';

import './forms.scss';
import { useNonPcnUpdateMutation } from '@api/services/non-pcn/hooks/useNonPcnUpdateMutation';
import { useFileStorageUploadMutation } from '@api/services/file-storage/hooks/useFileStorageUploadMutation';
import { useLookupTableFormVersions } from '@src/api/services/lookup/hooks';

const formData = {
  jd1a: {
    form: JDForm1a,
    metadata: () => getJDForm1aMetadata,
  },
  jd1b: {
    form: JDForm1b,
    metadata: () => getJDForm1bMetadata,
  },
  ip2a: {
    form: IPForm2a,
    metadata: getIpForm2aMetadata,
  },
  gp2b: {
    form: GPForm2b,
    metadata: getGPForm2bMetadata,
  },
  permit: {
    form: PermitForm,
    metadata: getPermitFormMetadata,
  },
  preApp: {
    form: PreAppForm3,
    metadata: () => getPreAppForm3Metadata,
  },
  violations: {
    form: ViolationsForm5,
    metadata: () => ViolationsForm5Metadata,
  },
  nonPcn: {
    form: NonPcnForm,
    metadata: () => getNonPcnFormMetadata,
  },
};

const formDataByRequestId = {
  [RequestTypeIds.AquaticResourceDelineationReport]: formData.jd1a,
  [RequestTypeIds.DelineationReport]: formData.jd1a,

  [RequestTypeIds.JurisdictionalDetermination]: formData.jd1b,

  [RequestTypeIds.ApplicationForDAPermit]: formData.ip2a,
  [RequestTypeIds.IndividualPermit]: formData.ip2a,

  [RequestTypeIds.PreconstructionNotification]: formData.gp2b,
  [RequestTypeIds.NationwidePermit]: formData.gp2b,

  [RequestTypeIds.PreApplication]: formData.preApp,
  [RequestTypeIds.ViolationComplaint]: formData.violations,

  [RequestTypeIds.NoPermitRequired]: formData.npr,
  [RequestTypeIds.ActivityBasedNoPermitRequired]: formData.abNpr,

  [RequestTypeIds.NonPreconstructionNotification]: formData.nonPcn,
  [RequestTypeIds.PermitApplication]: formData.permit,
};

const isGeneratePdfDisabled = (requestId) =>
  [
    RequestTypeStringIds.NoPermitRequired,
    RequestTypeStringIds.NonPreconstructionNotification,
    RequestTypeStringIds.ActivityBasedNoPermitRequired,
  ].includes(requestId);

const Forms = ({ isAnonymous, requestFormData, requestStatus }) => {
  const request = requestFormData?.request;
  const archived = requestFormData?.archived;

  const [initialSections, setInitialSections] = useState([]);
  const [activeForm, setActiveForm] = useState();
  const [activeFormMetadata, setActiveFormMetadata] = useState();
  const [activeSectionHeader, setActiveSectionHeader] = useState();
  const [previousSection, setPreviousSection] = useState();
  const [nextSection, setNextSection] = useState();
  const [activeRequestId, setActiveRequestId] = useState();

  const {
    addSections,
    getActiveSection,
    getSections,
    isFormReadOnly: isReadOnly,
    isNewRequest,
    selectedRequestId,
    setActiveSection,
    setIsFormReadOnly,
    updateSection,
  } = useRequest();

  const sections = getSections();
  const activeSection = getActiveSection();
  const [activeFormKey, setActiveFormKey] = useState();
  const lookupTableFormVersions = useLookupTableFormVersions();
  const reqId = Number(selectedRequestId);
  const resolvedRequestTypeId = mapToPermitApplicationRequestType(reqId, requestStatus);
  const onFirstSection = activeSection?.id === sections?.[0]?.id;
  const onLastSection = activeSection?.id === sections?.[sections?.length - 1]?.id;

  const canEditRequest = useUserRoleCapabilities((state) => state.roleCapabilities.editRequest);

  const { mutateAsync: updateNonPcn } = useNonPcnUpdateMutation();
  const { mutateAsync: uploadRequestFiles } = useFileStorageUploadMutation();
  const navigate = useNavigate();

  // @TODO: Add the rest of the request types as update logic gets migrated to React-Query/Zustand
  const updateFnMapping = {
    [RequestTypeIds.NonPreconstructionNotification]: updateNonPcn,
  };

  const handleOnSave = () => {
    saveRequest(false, uploadRequestFiles, updateFnMapping[reqId]);
  };

  useEffect(() => {
    const shouldBeReadOnly =
      !isAnonymous &&
      (request?.permission === UserPermissions.ReadOnly ||
        request?.requestStatus !== RequestStatus.Draft ||
        !canEditRequest ||
        archived);

    setIsFormReadOnly(shouldBeReadOnly);
  }, [canEditRequest, isAnonymous, request?.permission, request?.requestStatus, setIsFormReadOnly, archived]);

  useEffect(() => {
    const resolved = resolveFormDescriptor({
      requestTypeId: reqId,
      requestStatus: request?.requestStatus,
      storedFormVersion: request?.formVersion,
    });

    const legacy = formDataByRequestId[reqId]; // need this until all requests are migrated to registry

    const ActiveForm = resolved?.FormComponent ?? legacy?.form; //TODO: remove this once migrated all request to registry
    const metadata = resolved?.getMetadata?.() ?? legacy?.metadata?.();
    const header = RequestTypeStrings[reqId] || '';
    const nextFormKey = resolved?.formKey ?? `legacy:${reqId}`;

    if ((reqId !== activeRequestId || nextFormKey !== activeFormKey) && ActiveForm && metadata && header) {
      setActiveForm(
        resolved ? (
          <ActiveForm formKey={resolved.formKey} formVersion={resolved.version} formVariant={resolved.variant} />
        ) : (
          <ActiveForm />
        )
      );
      setActiveFormMetadata(metadata);
      setActiveSectionHeader(header);
      setActiveRequestId(resolvedRequestTypeId);
      setActiveFormKey(nextFormKey);
    }
  }, [
    activeSection,
    activeFormKey,
    activeRequestId,
    reqId,
    request?.requestStatus,
    request?.formVersion,
    resolvedRequestTypeId,
    lookupTableFormVersions,
  ]);

  //Is this for preapp?
  useEffect(() => {
    isAnonymous === null && navigate({ to: '/' });
  }, [isAnonymous, navigate]);

  //ONCE ACTIVE FORM IS SELECTED, INITIALIZE STEPPER AND SECTION VALIDATION STATE OBJECTS

  useEffect(() => {
    if (!activeForm) return;

    const sectionsToAdd = activeFormMetadata?.map((section, i) => ({
      id: i,
      status: i === 0 ? 'active' : '',
      touched: false,
      dirty: false,
      // ✅ Valid state is already in the section object
      valid: isReadOnly || i === 0 ? true : false,
      label: section.sectionName,
      isParentStep: !!section.isParentStep,
      isSubStep: !!section.isSubStep,
      parentId: section.parentId,
      hidden: false,
    }));
    setInitialSections(sectionsToAdd);
  }, [activeForm, activeFormMetadata, isReadOnly]);

  //SEND INITIAL STEPS TO STEPPER BUNDLE
  useEffect(() => {
    addSections(initialSections);
  }, [initialSections, addSections]);

  //REDIRECT WORKAROUND FOR PAGE REFRESH
  useEffect(() => {
    // @TODO: (Temporary Workaround) If user refreshes forms page, redirect to dashboard
    requestFormData.projectID === null && navigate({ to: '/dashboard' });
  }, [navigate, requestFormData.projectID]);

  // STEP VALIDATION LOGIC (CHECKS STATUS EACH SECTION REPORTS TO VALIDATION BUNDLE TOGGLES STEP TO DISPLAY STATUS ON STEPPER)
  useEffect(() => {
    if (!sections || !activeSection) return;

    sections.forEach((section) => {
      const isValid = section.valid; // ✅ Read directly from section object
      const isTouched = section.touched;
      const isActive = section.id === activeSection.id;

      if (isValid && isActive && section.status !== ErrorStates.ActiveComplete) {
        updateSection(section.id, { status: ErrorStates.ActiveComplete });
      } else if (isValid && !isActive && section.status !== ErrorStates.Complete) {
        updateSection(section.id, { status: ErrorStates.Complete });
      } else if (!isValid && isActive && isTouched && section.status !== ErrorStates.ActiveError) {
        updateSection(section.id, { status: ErrorStates.ActiveError });
      } else if (!isValid && !isActive && isTouched && section.status !== ErrorStates.Error) {
        updateSection(section.id, { status: ErrorStates.Error });
      }
    });
  }, [updateSection, sections, activeSection]);

  //VALIDATE ALL SECTIONS FOR FORMS IN PROGRESS ON RETURN
  useEffect(() => {
    if (sections && !isNewRequest) {
      sections.forEach((section, i) => {
        section.touched !== true && updateSection(i, { touched: true });
      });
    }
  }, [sections, sections.length, isNewRequest, updateSection]);

  // SETS TEXT ON NEXT AND PREVIOUS BUTTONS DYNAMICALLY

  useEffect(() => {
    if (activeSection) {
      const currentIndex = sections.findIndex((section) => section.id === activeSection.id);

      const findNextVisibleIndex = (index) => {
        const nextIndex = index + 1 < sections.length ? index + 1 : index;
        const nextSection = sections[nextIndex];
        if (nextSection && (nextSection.hidden || nextSection.isParentStep)) {
          return findNextVisibleIndex(nextIndex);
        }
        return nextIndex;
      };

      const findPreviousVisibleIndex = (index) => {
        const previousIndex = index - 1 >= 0 ? index - 1 : 0;
        const previousSection = sections[previousIndex];
        if (previousSection && (previousSection.hidden || previousSection.isParentStep)) {
          return findPreviousVisibleIndex(previousIndex);
        }
        return previousIndex;
      };

      const nextIndex = findNextVisibleIndex(currentIndex);
      const previousIndex = findPreviousVisibleIndex(currentIndex);

      setPreviousSection(sections[previousIndex]['label']);
      setNextSection(!onLastSection && sections[nextIndex] ? sections[nextIndex].label : '');
    }
  }, [activeSection, onLastSection, setNextSection, setPreviousSection, sections]);

  const handleNextClick = () => {
    if (!activeSectionHeader) return;

    const findNextVisibleSection = (sectionId) => {
      const nextSection = sections.find((section) => section.id === sectionId + 1);
      if (nextSection && (nextSection.hidden || nextSection.isParentStep)) {
        return findNextVisibleSection(nextSection.id);
      }
      return nextSection;
    };

    const nextSection = findNextVisibleSection(activeSection.id);
    if (!nextSection) return;

    if (nextSection.isParentStep) {
      updateSection(nextSection.id, { touched: true });
    }

    const newActiveSection = sections.find((section) => section.id === nextSection.id);
    !isReadOnly && handleOnSave();
    setActiveSection(newActiveSection);
    window.scrollTo(0, 0);
  };

  const handlePreviousClick = () => {
    if (!activeSection) return;

    const findPreviousVisibleSection = (sectionId) => {
      const previousSection = sections.find((section) => section.id === sectionId - 1);
      if (previousSection && (previousSection.hidden || previousSection.isParentStep)) {
        return findPreviousVisibleSection(previousSection.id);
      }
      return previousSection;
    };

    const previousSection = findPreviousVisibleSection(activeSection.id);
    if (!previousSection) return;

    if (previousSection.isSubStep) {
      updateSection(previousSection.parentId, { touched: true });
    }

    const newActiveSection = sections.find((section) => section.id === previousSection.id);
    !isReadOnly && handleOnSave();
    setActiveSection(newActiveSection);
    window.scrollTo(0, 0);
  };

  if (!activeSection) return null;

  return (
    <div>
      <FormHeader
        disableSaveProgress={isAnonymous}
        disableGeneratePdf={isGeneratePdfDisabled(selectedRequestId) || isAnonymous}
        header={activeSectionHeader}
      />
      <div className='display-flex'>
        <VerticalStepper />
        <div id='scroll' className='scroll-container'>
          {!onFirstSection ? (
            <SectionButton
              location='top'
              onClick={handlePreviousClick}
              onKeyUp={(e) => e.key === 'Enter' && handlePreviousClick(e)}
              section={previousSection}
            />
          ) : null}
          <div className='form-container'>{activeForm}</div>
          {!onLastSection ? (
            <SectionButton
              location='bottom'
              onClick={handleNextClick}
              onKeyUp={(e) => e.key === 'Enter' && handleNextClick(e)}
              section={nextSection}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default connect(
  'selectIsAnonymous',
  'selectRequestAPIData',
  'selectRequestFormData',
  'selectRequestStatus',
  Forms
);
